import type { EntitiesState } from '@redux/entities'

import type { TaskListItemModel } from '@models/types'

import useSelector from '@hooks/useSelector'
import useTaskListItems from '@hooks/useTaskListItems'

type FindUserTeamMemberParams = {
  assigneeId: number,
  entities: EntitiesState,
  teamId: number,
}

const findUserTeamMemberId = (params: FindUserTeamMemberParams) => {
  const { assigneeId, entities: { teamMembers }, teamId } = params

  const teamMember = Object.values(teamMembers).find((teamMember) => {
    const { team_id, user_id } = teamMember
    return team_id === teamId && user_id === assigneeId
  })

  return teamMember?.id
}

type GroupTasksParams = {
  entities: EntitiesState,
  filteredTaskListItems: TaskListItemModel[],
  groupKey: string,
  teamId: number,
}

type TaskGroup = {
  [key: string]: string | number,
  items: TaskListItemModel[],
}

export type GroupedTasks = {
  [key: number | string]: TaskGroup,
}

const groupTasks = (params: GroupTasksParams): GroupedTasks => {
  const {
    entities, filteredTaskListItems, groupKey, teamId,
  } = params

  const groupedTasks = filteredTaskListItems.reduce((acc: GroupedTasks, taskListItem) => {
    const groupKeyValue = taskListItem[groupKey]

    // Create Group
    if (!acc[groupKeyValue]){
      acc[groupKeyValue] = {
        [groupKey]: groupKeyValue,
        teamMemberId: findUserTeamMemberId({
          assigneeId: taskListItem.assignee_id,
          entities,
          teamId,
        }),
        items: [],
      }
    }

    // Add to Group
    acc[groupKeyValue].items.push(taskListItem)

    return acc
  }, {})

  return groupedTasks
}

type UseGroupedTasksOptions = {
  filters: {
    team_id?: number,
  },
  groupKey: string,
  performHttpRequests?: boolean,
  requestOptions?: object,
}

function useGroupedTasks(options: UseGroupedTasksOptions) {
  const {
    filters: {
      team_id: filterTeamId,
    },
    groupKey,
    performHttpRequests,
    requestOptions,
  } = options

  const entities = useSelector(reduxState => reduxState.entities)

  const taskListItemsPayload = useTaskListItems({
    filters: {
      completed: false,
      sub_tasks: true,
      template_project: true,
      team_id: filterTeamId,
    },
    performHttpRequests,
    requestOptions: {
      include_related_entity_subject: true,
      include_task_list_item_assignee: true,
      include_task_list_item_parent_task: true,
      include_task_list_item_related_entities: true,
      include_task_list_item_task_list: true,
      ...requestOptions,
    },
  })

  const { filteredTaskListItems, loading } = taskListItemsPayload

  const groupedTasks = groupTasks({
    entities,
    filteredTaskListItems,
    groupKey,
    teamId: filterTeamId,
  })

  return {
    entities,
    groupedTasks,
    loading,
  }
}

export default useGroupedTasks
